import type { HTTPError } from 'ky';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';

import { Button, Icon, type IconName, ModalDialog, showToast } from '~/chords';
import { Breadcrumbs } from '~/chords/Breadcrumbs';

import { useCreateCampaigns } from '../../api/useCreateCampaigns';
import { buildCampaignParams } from '../../utils/buildCampaignParams';
import { formatDate, formatUsd } from '../../utils/formatters';
import type { CreateCampaignFormValues } from '../CreateCampaignPage/createCampaignSchema';

function SectionHeading({
  icon,
  children,
}: {
  icon: IconName;
  children: React.ReactNode;
}) {
  return (
    <h2 className="flex items-center gap-sm text-headline-sm font-semibold">
      <Icon type={icon} size="md" />
      {children}
    </h2>
  );
}

SectionHeading.displayName = 'SectionHeading';

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-2xs">
      <span className="text-body-sm text-subtle">{label}</span>
      <span className="text-body-md font-medium">{children}</span>
    </div>
  );
}

Field.displayName = 'Field';

function LinkField({ label, url }: { label: string; url: string | undefined }) {
  if (!url) {
    return null;
  }

  return (
    <div className="flex flex-col gap-2xs">
      <span className="text-body-sm text-subtle">{label}</span>
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-xs text-body-md text-strong hover:underline"
      >
        <span className="truncate">{url}</span>
        <span className="shrink-0">
          <Icon type="arrow-up-right-from-square" size="sm" />
        </span>
      </a>
    </div>
  );
}

LinkField.displayName = 'LinkField';

export function ReviewCampaignPage() {
  const { t } = useTranslation('automated-creator-campaign');
  const navigate = useNavigate();
  const location = useLocation();
  const formValues = location.state?.formValues as
    | CreateCampaignFormValues
    | undefined;
  const { mutate: createCampaigns, isPending } = useCreateCampaigns();
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const handleSubmit = useCallback(() => {
    if (!formValues) {
      return;
    }

    const campaignParams = buildCampaignParams(formValues);

    createCampaigns(
      { campaign_params: campaignParams },
      {
        onSuccess: () => {
          setShowSuccessModal(true);
        },
        onError: async (error) => {
          let description = t('createCampaign.toast.errorDescription');

          try {
            const body = await (error as HTTPError).response.json<{
              detail?: string;
              message?: string;
            }>();
            const apiMessage = body?.detail ?? body?.message;

            if (typeof apiMessage === 'string' && apiMessage) {
              description = apiMessage;
            }
          } catch {
            // fall through to default description
          }

          showToast({
            type: 'error',
            title: t('createCampaign.toast.errorTitle'),
            description,
          });
        },
      },
    );
  }, [formValues, createCampaigns, t]);

  const handleSuccessOk = useCallback(() => {
    setShowSuccessModal(false);
    navigate('/marketing/automated-creator-campaigns');
  }, [navigate]);

  if (!formValues) {
    return <Navigate to="/marketing/automated-creator-campaigns/new" replace />;
  }

  const clippingDisplay =
    formValues.clippingEnabled && formValues.clippingBudget
      ? formatUsd(formValues.clippingBudget)
      : '--';

  const influencerDisplay =
    formValues.influencerEnabled && formValues.influencerBudget
      ? formatUsd(formValues.influencerBudget)
      : '--';

  const totalDollars =
    (formValues.clippingEnabled ? (formValues.clippingBudget ?? 0) : 0) +
    (formValues.influencerEnabled ? (formValues.influencerBudget ?? 0) : 0);

  const totalDisplay = totalDollars > 0 ? formatUsd(totalDollars) : '--';

  return (
    <div className="max-w-[2048px] p-xl">
      <Breadcrumbs.Root>
        <Breadcrumbs.Item to="/marketing">
          {t('breadcrumb.marketing')}
        </Breadcrumbs.Item>
        <Breadcrumbs.Item to="/marketing/automated-creator-campaigns">
          {t('breadcrumb.current')}
        </Breadcrumbs.Item>
        <Breadcrumbs.Item>{t('reviewCampaign.breadcrumb')}</Breadcrumbs.Item>
      </Breadcrumbs.Root>

      <h1 className="mt-md text-display-sm font-bold">
        {t('createCampaign.title')}
      </h1>

      <div className="mt-xl rounded-sm border bg-emphasis-default border-emphasis-subtle">
        {/* Campaign details + Track details + Links */}
        <div className="grid grid-cols-3 gap-xl p-lg">
          <section className="flex flex-col gap-lg border-r pr-xl border-emphasis-subtle">
            <SectionHeading icon="calendar-check">
              {t('createCampaign.sections.campaignDetails')}
            </SectionHeading>

            <Field label={t('createCampaign.fields.startDate')}>
              {formatDate(formValues.campaignStartDate, 'long')}
            </Field>

            <Field label={t('createCampaign.fields.endDate')}>
              {formatDate(formValues.campaignEndDate, 'long')}
            </Field>

            <Field label={t('createCampaign.fields.advertiser')}>
              {formValues.advertiser}
            </Field>
          </section>

          <section className="flex flex-col gap-lg border-r pr-xl border-emphasis-subtle">
            <SectionHeading icon="music">
              {t('createCampaign.sections.trackDetails')}
            </SectionHeading>

            <Field label={t('createCampaign.fields.artist')}>
              {formValues.artist}
            </Field>

            <Field label={t('createCampaign.fields.track')}>
              {formValues.track}
            </Field>

            <Field label={t('createCampaign.fields.isrc')}>
              {formValues.isrc}
            </Field>
          </section>

          <section className="flex flex-col gap-lg">
            <SectionHeading icon="link">
              {t('createCampaign.sections.links')}
            </SectionHeading>

            <LinkField
              label={t('reviewCampaign.fields.spotifyLink')}
              url={formValues.spotifyUrl}
            />

            <LinkField
              label={t('createCampaign.fields.tiktokLink')}
              url={formValues.tiktokLink}
            />

            <LinkField
              label={t('createCampaign.fields.instagramLink')}
              url={formValues.instagramLink}
            />
          </section>
        </div>

        <hr className="border-emphasis-subtle" />

        {/* Channel and budget */}
        <div className="flex flex-col gap-lg p-lg">
          <SectionHeading icon="circle-dollar">
            {t('createCampaign.sections.channelAndBudget')}
          </SectionHeading>

          <div className="grid grid-cols-[1fr_1fr_auto] gap-xl">
            {/* Clipping channel */}
            <div className="flex flex-col gap-lg border-r pr-xl border-emphasis-subtle">
              <span className="text-body-md font-semibold">
                {t('createCampaign.fields.clippingCampaign')}
              </span>
              <p className="text-body-sm text-subtle">
                {t('createCampaign.fields.clippingDescription')}
              </p>
              <Field label={t('createCampaign.fields.spend')}>
                {clippingDisplay}
              </Field>
              {formValues.clippingEnabled &&
              formValues.clippingPurchaseOrder ? (
                <Field label={t('createCampaign.fields.clippingPurchaseOrder')}>
                  {formValues.clippingPurchaseOrder}
                </Field>
              ) : null}
            </div>

            {/* Influencer channel */}
            <div className="flex flex-col gap-lg border-r pr-xl border-emphasis-subtle">
              <span className="text-body-md font-semibold">
                {t('createCampaign.fields.influencerCampaign')}
              </span>
              <p className="text-body-sm text-subtle">
                {t('createCampaign.fields.influencerDescription')}
              </p>
              <Field label={t('createCampaign.fields.spend')}>
                {influencerDisplay}
              </Field>
              {formValues.influencerEnabled &&
              formValues.influencerPurchaseOrder ? (
                <Field
                  label={t('createCampaign.fields.influencerPurchaseOrder')}
                >
                  {formValues.influencerPurchaseOrder}
                </Field>
              ) : null}
            </div>

            {/* Spend summary */}
            <div className="flex w-[180px] flex-col justify-end gap-md">
              <div className="flex flex-col gap-xs">
                <span className="text-body-sm text-subtle">
                  {t('createCampaign.fields.clippingSpend')}
                </span>
                <span className="text-headline-sm font-semibold">
                  {clippingDisplay}
                </span>
              </div>
              <div className="flex flex-col gap-xs">
                <span className="text-body-sm text-subtle">
                  {t('createCampaign.fields.influencerSpend')}
                </span>
                <span className="text-headline-sm font-semibold">
                  {influencerDisplay}
                </span>
              </div>
              <div className="flex flex-col gap-xs rounded-sm p-md text-right bg-emphasis-subtle">
                <span className="text-body-sm font-medium">
                  {t('createCampaign.fields.totalSpend')}
                </span>
                <span className="text-headline-sm font-semibold">
                  {totalDisplay}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="mt-xl flex flex-col items-end gap-sm">
        <div className="flex items-center gap-md">
          <Button
            variant="text"
            size="large"
            onClick={() =>
              navigate('/marketing/automated-creator-campaigns/new', {
                state: { formValues },
              })
            }
            disabled={isPending}
          >
            {t('reviewCampaign.actions.cancel')}
          </Button>
          <Button
            variant="primary"
            size="large"
            onClick={handleSubmit}
            disabled={isPending}
          >
            {t('reviewCampaign.actions.submit')}
          </Button>
        </div>
        <p className="text-body-sm text-subtle">
          {t('reviewCampaign.disclaimer')}
        </p>
      </div>

      {/* Success modal */}
      <ModalDialog open={showSuccessModal} onOpenChange={setShowSuccessModal}>
        <ModalDialog.Header>
          {t('reviewCampaign.successModal.title')}
        </ModalDialog.Header>
        <ModalDialog.Body>
          <p>{t('reviewCampaign.successModal.description')}</p>
        </ModalDialog.Body>
        <ModalDialog.Footer>
          <Button variant="primary" onClick={handleSuccessOk}>
            {t('reviewCampaign.successModal.ok')}
          </Button>
        </ModalDialog.Footer>
      </ModalDialog>
    </div>
  );
}

ReviewCampaignPage.displayName = 'ReviewCampaignPage';
